// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, AfterViewInit, ViewChild, ElementRef, Input, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { AppEvent, Dimensions } from '@interfaces/interfaces';
import SignaturePad, { Options, PointGroup } from 'signature_pad';

export interface SignaturePadOptions extends Options {
    canvasHeight: number;
    canvasWidth: number;
}

@Component({
    selector: 'app-signature-capture',
    templateUrl: './signature-capture.component.html',
    styleUrls: ['./signature-capture.component.scss'],
})
export class SignatureCaptureComponent implements AfterViewInit, OnChanges {
    @ViewChild('canvas', { static: false }) public canvas: ElementRef;
    @ViewChild('hintTextCanvas', { static: false }) public hintTextCanvas: ElementRef;
    @Input() error = false;
    @Input() hideButton = true;
    @Input() id = 'displayCanvas';
    @Input() dimensions: Dimensions;
    @Input() newImage: any;
    @Input() blobImage: any;
    @Input() pointGroups: PointGroup[];
    @Input() ignoreBlobNewSignatureInProgress: boolean;
    @Input() canEdit = true;
    @Input() hintText: string = null;
    @Output() public outEvent: EventEmitter<AppEvent> = new EventEmitter();

    private signaturePad: SignaturePad;
    private croppedImage: string = null;
    private points = [];
    private mimeType = 'image/svg';
    private originalSet = false;
    // Used to keep track of the scale of the points in case the screen size changes
    // or the user is on a mobile device
    private ratio: number;

    public image: any;
    public labelText: string;
    public options: SignaturePadOptions;

    constructor() {
        this.options = {
            canvasHeight: this.dimensions?.height,
            canvasWidth: this.dimensions?.width,
            throttle: 0,
        } as SignaturePadOptions;
    }

    ngAfterViewInit(): void {
        this.drawSignatureHints();
        if (this.dimensions.height) {
            this.setCanvasHeight(this.dimensions.height);
        }

        if (this.dimensions.width) {
            this.setCanvasWidth(this.dimensions.width);
        }

        if (this.canEdit && window.innerWidth < 705) {
            this.handleCanvasResizeSetSignatures();
        }

        // this option can be omitted if only saving as PNG or SVG
        // If this isn't there it saves the image as a black square
        //(this.options || ({} as SignaturePadOptions)).backgroundColor = 'rgb(255, 255, 255)';

        const canvas = this.canvas.nativeElement;
        this.signaturePad = new SignaturePad(canvas, this.options);
        // We only care about whena  stroke is finished in order to export the cropped image properly
        this.signaturePad.addEventListener('endStroke', (event: CustomEvent) => this.endStroke(event));

        if (!this.hideButton) {
            document.getElementById(`clear-${this.id}`).addEventListener('click', this.clearEventHandler);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.pointGroups && changes.pointGroups?.currentValue) {
            this.setSignatureImage(changes.pointGroups.currentValue);
        } else if (changes.newImage?.currentValue && Array.isArray(changes.newImage?.currentValue)) {
            // The user is resetting the image if it gets into here.
            // We need to clear the signature then refresh
            this.signaturePad.clear();
            this.setSignatureImage(changes.newImage.currentValue);
        } else if (changes.newImage?.currentValue === null && this.pointGroups) {
            this.setSignatureImage(this.pointGroups);
        } else {
            // new signature, no previous data
            this.drawSignatureHints();
        }

        this.determineCanvasLabels();

        // Make sure this only gets called for future changes and not initial.
        // Canvas might not have loaded yet, so we handle the initial inside AfterViewInit to verify canvas is there.
        if (changes.canEdit?.currentValue && !changes.canEdit?.firstChange && window.innerWidth < 705) {
            this.handleCanvasResizeSetSignatures();
        }
    }

    setCanvasHeight(height: number) {
        const canvas = this.getCanvas();
        const hintCanvas = this.getHintCanvas();
        canvas.height = height;
        hintCanvas.height = height;
    }

    setCanvasWidth(width: number) {
        const canvas = this.getCanvas();
        const hintCanvas = this.getHintCanvas();
        canvas.width = width;
        hintCanvas.width = width;
    }

    public resizeCanvas() {
        const canvas = this.getCanvas();
        if (canvas.offsetWidth < canvas.width) {
            this.ratio = canvas.offsetWidth / canvas.width;
            this.setCanvasWidth(canvas.offsetWidth);
            this.setCanvasHeight(canvas.height * this.ratio);
        }
    }

    public handleCanvasResizeSetSignatures() {
        this.resizeCanvas();
        if (this.pointGroups) {
            this.setSignatureImage(this.pointGroups);
        }
    }

    public scalePoints(points: PointGroup[], down = true) {
        if (points) {
            points = JSON.parse(JSON.stringify({ points })).points;
            if (this.ratio === undefined || this.ratio === 0) {
                return points;
            }

            // Scale the point values down to match the screen size
            // and scale up to standardize the saved sizes of the images
            for (const stroke of points) {
                stroke.points = stroke.points.map((point) => {
                    return {
                        ...point,
                        x: down ? point.x * this.ratio : point.x / this.ratio,
                        y: down ? point.y * this.ratio : point.y / this.ratio,
                    };
                });
            }
        }

        return points;
    }

    async setSignatureImage(points: PointGroup[]) {
        if (this.ratio) {
            points = this.scalePoints(points);
        }

        this.drawSignatureHints();

        this.signaturePad.fromData(points, { clear: false });
        this.points = this.signaturePad.toData().reduce((arr, current) => {
            return arr.concat(current.points);
        }, []);

        this.cropSignatureCanvas();
        this.updateSignature();
    }

    private drawSignatureHints = () => {
        if (null === this.hintText) {
            return;
        }

        const canvas = this.getHintCanvas();
        if (!canvas) {
            // not ready yet
            return;
        }
        const lineWidth = canvas.width;
        const lineMargin = 10;
        const lineYpos = (canvas.height * 3) / 4;
        const font = '22px Sans rgb(26, 66, 138)';
        const style = 'rgb(26, 66, 138)';
        this.drawLine({ x: lineMargin, y: lineYpos, w: lineWidth - 2 * lineMargin, h: 3, style });
        this.drawText({ x: lineMargin + 2, y: lineYpos - 5, text: 'X', font, style });
        this.drawText({ x: lineMargin + 100, y: lineYpos - 5, text: this.hintText, font, style });
    };

    private drawLine = ({ x = 0, y = 0, w = 100, h = 10, style = 'black' }) => {
        const canvas = this.getHintCanvas();
        const context = canvas.getContext('2d');
        context.fillStyle = style;

        context.fillRect(x, y, w, h);
    };

    private drawText = ({ x = 0, y = 0, text = '', font = '', style = '' }) => {
        const canvas = this.getHintCanvas();
        const context = canvas.getContext('2d');
        context.font = font;
        context.fillStyle = style;
        context.fillText(text, x, y);
    };

    private base64ToBlob = (base64: string, mime: string): Blob => {
        const byteString = window.atob(base64.split(',')[1]);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const unitArray = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
            unitArray[i] = byteString.charCodeAt(i);
        }

        return new Blob([arrayBuffer], { type: mime });
    };

    public clearCanvas() {
        this.signaturePad.clear(); // otherwise isEmpty() might return incorrect value

        this.outEvent.emit({
            context: `clear-${this.id}`,
            data: null,
        });
    }

    private clearEventHandler = () => {
        this.clearCanvas();
    };

    private determineCanvasLabels(): void {
        const canvasType = this.id.split('-');
        this.labelText = `${canvasType[0].charAt(0).toUpperCase()}${canvasType[0].slice(1)}`;
    }

    private cropSignatureCanvas() {
        // 1. duplicate the canvas to not alter the original
        const canvas = this.canvas.nativeElement;
        const baseContext = canvas.getContext('2d');
        const croppedCanvas = document.createElement('canvas');
        const croppedCtx = croppedCanvas.getContext('2d');

        croppedCanvas.width = canvas.width;
        croppedCanvas.height = canvas.height;
        croppedCtx.drawImage(canvas, 0, 0);

        // 2. Get the min and max X and Y values of strokes
        let minX = null;
        let maxX = null;
        let minY = null;
        let maxY = null;

        for (const point of this.points) {
            //set the minimum X value
            if (point.x < minX || minX === null) {
                minX = point.x;
            }

            // set the maximum X Value
            if (point.x > maxX || maxX === null) {
                maxX = point.x;
            }

            //set the minimum Y value
            if (point.y < minY || minY === null) {
                minY = point.y;
            }

            // set the maximum X Value
            if (point.y > maxY || maxY === null) {
                maxY = point.y;
            }
        }

        // 3. Create new contrsaints and extract signature from canvas
        const newWidth = maxX - minX;
        const newHeight = maxY - minY;

        // Prevent cropping the signature if the user extends past the canvas borders
        // - Cropping an image outside of the bounds starts causing weird extra lines in direction
        //   that was cropped
        if (newWidth > canvas.width || newHeight > canvas.height) {
            this.croppedImage = null;
            return;
        }

        if (newWidth > 0 && newHeight > 0) {
            const cut = croppedCtx.getImageData(minX, minY, newWidth, newHeight);

            // 4. Re-size new canvas with new restraints and paste signature onto it
            croppedCanvas.width = newWidth;
            croppedCanvas.height = newHeight;
            croppedCtx.putImageData(cut, 0, 0);
        }

        this.croppedImage = croppedCanvas.toDataURL(this.mimeType);
    }

    private releaseEventHandler = () => {
        this.points = [];

        // reset the points of the signature to figure out cropping
        this.points = this.signaturePad.toData().reduce((arr, current) => {
            return arr.concat(current.points);
        }, []);

        this.cropSignatureCanvas();
        this.updateSignature();
    };

    // scale the points to match our standard sizes
    private updateSignature(): void {
        const dataUrl = this.signaturePad.toDataURL(this.mimeType);

        // CREATING AND SAVING A NEW IMAGE - THE NEW IMAGE IS THE CROPPEDIMAGE
        const imageToSend = this.croppedImage ? this.base64ToBlob(this.croppedImage, this.mimeType) : this.base64ToBlob(dataUrl, this.mimeType);
        const pointData = this.getPointData();

        this.outEvent.emit({
            context: this.id,
            data: {
                image: imageToSend,
                // Store the signature_pad data directly for now to maintain
                // how the signature looks
                pointData: pointData,
                scaledData: this.ratio < 1 && this.originalSet ? this.scalePoints(pointData, false) : undefined,
                fileName: 'digital-signature.svg',
            },
        });

        if (!this.originalSet && this.ratio) {
            this.originalSet = true;
        }
    }

    private getPointData() {
        const pointData = this.signaturePad.toData();
        return pointData;
    }

    public getCanvas(): HTMLCanvasElement {
        return this.canvas?.nativeElement;
    }
    public getHintCanvas(): HTMLCanvasElement {
        return this.hintTextCanvas?.nativeElement;
    }

    public endStroke(event: any) {
        event.preventDefault();
        this.releaseEventHandler();
    }
}
